import { ApolloClient } from '@apollo/client';
import gql from 'graphql-tag';

import { UserInfo, VendorUserInfo } from '../../../src/rfxportal/pages/RFXRequest/steps/RFXRequestSuppliersStep/types';

/**
 * Create User query for UMS
 */
export const createUserQuery = gql`
  query createUser(
    $userType: PS_UserType
    $username: String
    $emails: [Email]
    $givenName: String
    $familyName: String
    $vendorIds: [String]
    $applications: [String]
    $entitlements: [PS_Entitlement]
  ) {
    createUser(
      userType: $userType
      username: $username
      emails: $emails
      givenName: $givenName
      familyName: $familyName
      vendorIds: $vendorIds
      applications: $applications
      entitlements: $entitlements
    ) {
      username
      userId
    }
  }
`;

/**
 * Represents the result of the createUser query.
 *
 * @typedef {Object} CreateVendorUserResult
 * @property {string} username - Username of created user.
 * @property {string} userId - ID of created user.
 */
export interface CreateVendorUserResult {
  username: string;
  userId: string;
}

// Constants for the createVendorUser query
const SMART_APPLICATION = ['SMART'];
const VENDOR_USERTYPE = 'VENDOR_EMPLOYEE';
const ARGO_ENTITLEMENT = 'ARGOACCESS';

/**
 * Creates a vendor user in the User Management Service (UMS).
 *
 * @param {ApolloClient<object>} client - Instance of ApolloClient used to execute query.
 * @param {UserInfo} user - User information for creating vendor user.
 * @returns {Promise<CreateVendorUserResult>} - Resolves with the CreateVendorUserResult object containing created username and ID.
 */
export async function createVendorUser(client: ApolloClient<object>, user: UserInfo): Promise<CreateVendorUserResult> {
  const queryVariables = {
    userType: VENDOR_USERTYPE,
    username: user.email,
    givenName: user.givenName,
    familyName: user.familyName,
    vendorIds: user.businessEntities,
    emails: [
      {
        value: user.email,
        isPrimary: true
      }
    ],
    applications: SMART_APPLICATION,
    entitlements: [ARGO_ENTITLEMENT]
  };

  const response = await client.query({
    query: createUserQuery,
    variables: queryVariables
  });

  return response.data.createUser;
}
